import React, { useState, useEffect } from 'react';
import {
    Box,
    Drawer,
    Typography,
    IconButton,
    Button,
    Chip,
    Divider,
    Stack,
    Avatar,
    Grid,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Alert,
    Skeleton,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Close as CloseIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Schedule as ScheduleIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    AccessTime as AccessTimeIcon,
    Visibility as VisibilityIcon,
    ThumbUp as ThumbUpIcon,
    Comment as CommentIcon,
    Share as ShareIcon,
    Folder as FolderIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import RenderSocialIcons from './RenderSocialIcons';
import { useAuth } from '../providers/AuthProvider';
import RescheduleDialog from './RescheduleDialog';
import { useTracking } from '../hooks/useTracking';
import { EnhancedPlatformEvents } from '../services/analytics/events';
import {
  formatScheduledDate,
  getUserTimezone,
} from '../utils/schedulingHelpers';

const DrawerContent = styled(Box)(({ theme }) => ({
    width: '100%',
    maxWidth: 480,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.default,
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
}));

const DrawerBody = styled(Box)(({ theme }) => ({
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(3),
}));

const DrawerFooter = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
}));

const StatusBadge = styled(Chip)(({ theme, status }) => ({
    fontWeight: 'medium',
    '& .MuiChip-icon': {
        fontSize: '1rem',
    },
    ...(status === 'posted' && {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.contrastText,
    }),
    ...(status === 'scheduled' && {
        backgroundColor: theme.palette.info.light,
        color: theme.palette.info.contrastText,
    }),
    ...(status === 'error' && {
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.contrastText,
    }),
}));

const PlatformCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
}));

const ContentBucketChip = styled(Chip)(({ theme }) => ({
    margin: theme.spacing(0.5),
    '& .MuiChip-label': {
        fontWeight: 'medium',
    },
}));

const EngagementCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    textAlign: 'center',
    border: `1px solid ${theme.palette.divider}`,
}));

/**
 * PostDetailsSlideover Component
 * 
 * A comprehensive slide-out panel that displays detailed information about a post,
 * including platform details, media, scheduling information, engagement metrics,
 * and content bucket assignments.
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the slideover is open
 * @param {Function} props.onClose - Callback when slideover is closed
 * @param {Object} props.post - The post object to display details for
 */
const PostDetailsSlideover = ({ open, onClose, post, onPostUpdate = null }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const { user } = useAuth();
    const { trackEvent } = useTracking();
    
    // State management
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
    const [threadPosts, setThreadPosts] = useState([]);
    const [loadingThreadPosts, setLoadingThreadPosts] = useState(false);

    // Effect to fetch thread posts when opening a thread parent
    useEffect(() => {
        const fetchThreadPosts = async () => {
            // Only fetch if this is a parent thread post
            console.log(post)
            if (!post || !post.is_thread || post.thread_order !== 1 || !open) {
                setThreadPosts([]);
                console.log("++++++++++++", `${process.env.REACT_APP_API_BASE_URL}post/posts/?parent_thread=${post?.id}`)
                return;
            }

            setLoadingThreadPosts(true);
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}post/posts/?parent_thread=${post.id}`,
                    {
                        headers: {
                            'Authorization': `Token ${localStorage.getItem('token')}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );
                console.log("++++++++++++", response)

                if (response.ok) {
                    const data = await response.json();
                    console.log("Thread posts response:", data);
                    
                    // Handle both paginated and non-paginated responses
                    const posts = data.results || data;
                    
                    // Filter out the parent post itself and sort by thread_order
                    const childPosts = (Array.isArray(posts) ? posts : [])
                        .filter(p => p.id !== post.id && p.thread_order > 1)
                        .sort((a, b) => a.thread_order - b.thread_order);
                    
                    console.log(`Found ${childPosts.length} child posts for thread ${post.id}`);
                    setThreadPosts(childPosts);
                } else {
                    const errorData = await response.json();
                    console.error('Failed to fetch thread posts:', response.status, errorData);
                    setThreadPosts([]);
                }
            } catch (error) {
                console.error('Error fetching thread posts:', error);
                setThreadPosts([]);
            } finally {
                setLoadingThreadPosts(false);
            }
        };
        
        fetchThreadPosts();
    }, [post, open]);
    
    // Return null if no post
    if (!post) {
        return null;
    }

    // Helper functions
    const getPostStatus = () => {
        const hasError = post.platform_details?.some(detail => detail.error || detail.failed);
        const isScheduled = post.is_scheduled_post || dayjs(post.scheduled_post_date).isAfter(dayjs());
        const isPosted = post.platform_details?.every(detail => detail.is_posted);

        if (hasError) return 'error';
        if (isPosted) return 'posted';
        if (isScheduled) return 'scheduled';
        return 'draft';
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'posted':
                return <CheckCircleIcon />;
            case 'scheduled':
                return <AccessTimeIcon />;
            case 'error':
                return <ErrorIcon />;
            default:
                return <ScheduleIcon />;
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'posted':
                return 'Posted';
            case 'scheduled':
                return 'Scheduled';
            case 'error':
                return 'Error';
            default:
                return 'Draft';
        }
    };

    const getPlatformDisplayName = (platform) => {
        const platformNames = {
            'twitter': 'Twitter',
            'facebook': 'Facebook',
            'instagram': 'Instagram',
            'linkedin': 'LinkedIn',
            'youtube': 'YouTube',
            'tiktok': 'TikTok',
            'bluesky': 'Bluesky',
            'mastodon': 'Mastodon',
            'threads': 'Threads'
        };
        
        return platformNames[platform?.toLowerCase()] || platform;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'Not scheduled';
        return dayjs(dateString).format('MMM D, YYYY at h:mm A');
    };

    // Event handlers
    const handleEdit = () => {
        // Navigate to compose page with post data
        console.log('[PostDetailsSlideover] Navigating to edit mode with post:', post);
        console.log('[PostDetailsSlideover] Post structure:', {
            id: post.id,
            post_text: post.post_text,
            scheduled_post_date: post.scheduled_post_date,
            platform_details: post.platform_details,
            img_url: post.img_url,
            content_buckets: post.content_buckets
        });
        
        navigate('/post/create', { 
            state: { 
                editMode: true, 
                postData: post 
            } 
        });
        onClose();
    };
    
    const handleReschedule = () => {
        // Track reschedule dialog opened
        trackEvent(EnhancedPlatformEvents.RESCHEDULE_DIALOG_OPENED.name, {
            postId: post.id,
            isThread: post.is_thread,
            platforms: post.platform_details?.map(pd => pd.platform) || [],
            currentStatus: getPostStatus()
        });
        
        setRescheduleDialogOpen(true);
    };

    // Handle successful reschedule
    const handleRescheduleSuccess = (result) => {
        // Update post data if callback provided
        if (typeof onPostUpdate === 'function') {
            onPostUpdate(result);
        }
        
        // Close the reschedule dialog
        setRescheduleDialogOpen(false);
        
        // Optionally close the details slideover as well
        // onClose();
    };

    const handleDeleteClick = () => {
        setDeleteDialogOpen(true);
        setDeleteError(null);
    };

    const handleDeleteConfirm = async () => {
        setDeleting(true);
        setDeleteError(null);

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}post/posts/${post.id}/`,
                {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Token ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                throw new Error('Failed to delete post');
            }

            // Close dialogs and refresh
            setDeleteDialogOpen(false);
            onClose();
            
            // Optionally trigger a refresh of the posts list
            window.location.reload();
            
        } catch (error) {
            console.error('Error deleting post:', error);
            setDeleteError('Failed to delete post. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setDeleteError(null);
    };

    // Get current status
    const currentStatus = getPostStatus();
    const canEdit = currentStatus === 'scheduled' || currentStatus === 'draft';

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 480 },
                        maxWidth: '100vw',
                    }
                }}
            >
                <DrawerContent>
                    {/* Header */}
                    <DrawerHeader>
                        <Box>
                            <Typography variant="h6" component="h2">
                                Post Details
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                ID: {post.id}
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} aria-label="Close panel">
                            <CloseIcon />
                        </IconButton>
                    </DrawerHeader>

                    {/* Body */}
                    <DrawerBody>
                        <Stack spacing={3}>
                            {/* Status Section */}
                            <Box>
                                <StatusBadge
                                    icon={getStatusIcon(currentStatus)}
                                    label={getStatusLabel(currentStatus)}
                                    status={currentStatus}
                                    size="medium"
                                />
                            </Box>

                            {/* Thread Badge if applicable */}
                            {post.is_thread && post.thread_order === 1 && (
                                <Box>
                                    <Chip
                                        label={`Thread (${threadPosts.length + 1} posts)`}
                                        color="primary"
                                        variant="outlined"
                                        sx={{ mb: 2 }}
                                    />
                                </Box>
                            )}

                            {/* Content Section */}
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Content
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                                    {post.content || post.post_text}
                                </Typography>
                                
                                {/* Media */}
                                {post.img_url && (
                                    <Box sx={{ mb: 2 }}>
                                        <img
                                            src={post.img_url}
                                            alt={post.img_alt_text || 'Post media'}
                                            style={{
                                                width: '100%',
                                                maxHeight: 300,
                                                objectFit: 'cover',
                                                borderRadius: 8,
                                            }}
                                        />
                                        {post.img_alt_text && (
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                Alt text: {post.img_alt_text}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>

                            {/* Timing Information */}
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Timing
                                </Typography>
                                <Stack spacing={1}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Created:
                                        </Typography>
                                        <Typography variant="body2">
                                            {formatDateTime(post.created_at)}
                                        </Typography>
                                    </Box>
                                    {post.scheduled_post_date && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Scheduled:
                                            </Typography>
                                            <Typography variant="body2">
                                                {formatDateTime(post.scheduled_post_date)}
                                            </Typography>
                                        </Box>
                                    )}
                                    {post.platform_details?.some(detail => detail.posted_at) && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Posted:
                                            </Typography>
                                            <Typography variant="body2">
                                                {formatDateTime(post.platform_details.find(detail => detail.posted_at)?.posted_at)}
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>
                            </Box>

                            {/* Platform Details */}
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Platforms
                                </Typography>
                                <Stack spacing={1}>
                                    {post.platform_details?.map((detail, index) => (
                                        <PlatformCard key={index} elevation={0}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography variant="subtitle2" fontWeight="medium">
                                                    {getPlatformDisplayName(detail.platform)}
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    label={detail.is_posted ? 'Posted' : 'Pending'}
                                                    color={detail.is_posted ? 'success' : 'default'}
                                                />
                                            </Box>
                                            {detail.platform_specific_text && detail.platform_specific_text !== post.post_text && (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                    Platform text: {detail.platform_specific_text}
                                                </Typography>
                                            )}
                                            {detail.platform_post_url && (
                                                <Button
                                                    size="small"
                                                    startIcon={<VisibilityIcon />}
                                                    href={detail.platform_post_url}
                                                    target="_blank"
                                                    sx={{ mt: 1 }}
                                                >
                                                    View Post
                                                </Button>
                                            )}
                                        </PlatformCard>
                                    ))}
                                </Stack>
                            </Box>

                            {/* Thread Posts Section */}
                            {post.is_thread && post.thread_order === 1 && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        Thread Posts
                                    </Typography>
                                    {loadingThreadPosts ? (
                                        <Stack spacing={2}>
                                            {[1, 2].map((n) => (
                                                <Skeleton key={n} variant="rectangular" height={100} />
                                            ))}
                                        </Stack>
                                    ) : threadPosts.length > 0 ? (
                                        <Stack spacing={2}>
                                            {threadPosts.map((threadPost, index) => (
                                            <Paper 
                                                key={threadPost.id} 
                                                elevation={0}
                                                sx={{ 
                                                    p: 2, 
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderLeft: '3px solid',
                                                    borderLeftColor: 'primary.main',
                                                    backgroundColor: 'background.paper'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography variant="subtitle2" color="primary" fontWeight="medium">
                                                        Post {index + 2} of {threadPosts.length + 1}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        ID: {threadPost.id}
                                                    </Typography>
                                                </Box>
                                                
                                                <Typography variant="body2" sx={{ mb: threadPost.img_url ? 1.5 : 0, lineHeight: 1.6 }}>
                                                    {threadPost.content || threadPost.post_text}
                                                </Typography>
                                                
                                                {threadPost.img_url && (
                                                    <Box>
                                                        <img
                                                            src={threadPost.img_url}
                                                            alt={threadPost.img_alt_text || 'Thread media'}
                                                            style={{
                                                                width: '100%',
                                                                maxHeight: 200,
                                                                objectFit: 'cover',
                                                                borderRadius: 4,
                                                            }}
                                                        />
                                                        {threadPost.img_alt_text && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                                Alt text: {threadPost.img_alt_text}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )}
                                                
                                                {/* Platform details for thread post */}
                                                {threadPost.platform_details && threadPost.platform_details.length > 0 && (
                                                    <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                        {threadPost.platform_details.map((detail, idx) => (
                                                            <Chip
                                                                key={idx}
                                                                size="small"
                                                                label={getPlatformDisplayName(detail.platform)}
                                                                color={detail.is_posted ? 'success' : 'default'}
                                                                variant="outlined"
                                                                sx={{ fontSize: '0.75rem' }}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                            </Paper>
                                        ))}
                                    </Stack>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            No thread posts found
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            {/* Content Buckets */}
                            {post.content_buckets && post.content_buckets.length > 0 && (
                                <Box>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <FolderIcon fontSize="small" />
                                        Content Buckets
                                    </Typography>
                                    <Box>
                                        {post.content_buckets.map((bucket, index) => (
                                            <ContentBucketChip
                                                key={index}
                                                label={bucket.bucket_name}
                                                size="small"
                                                variant="outlined"
                                                sx={{ 
                                                    borderColor: bucket.bucket_color,
                                                    color: bucket.bucket_color,
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {/* Engagement Metrics (Placeholder) */}
                            {currentStatus === 'posted' && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        Engagement
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={4}>
                                            <EngagementCard elevation={0}>
                                                <VisibilityIcon color="primary" sx={{ mb: 1 }} />
                                                <Typography variant="h6">--</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Views
                                                </Typography>
                                            </EngagementCard>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <EngagementCard elevation={0}>
                                                <ThumbUpIcon color="primary" sx={{ mb: 1 }} />
                                                <Typography variant="h6">--</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Likes
                                                </Typography>
                                            </EngagementCard>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <EngagementCard elevation={0}>
                                                <ShareIcon color="primary" sx={{ mb: 1 }} />
                                                <Typography variant="h6">--</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Shares
                                                </Typography>
                                            </EngagementCard>
                                        </Grid>
                                    </Grid>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                                        Engagement data coming soon
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                    </DrawerBody>

                    {/* Footer Actions */}
                    <DrawerFooter>
                        <Stack direction="row" spacing={2} justifyContent="flex-end" flexWrap="wrap">
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={handleDeleteClick}
                                size={isMobile ? "medium" : "small"}
                            >
                                Delete
                            </Button>
                            {canEdit && currentStatus === 'scheduled' && (
                                <Button
                                    variant="outlined"
                                    startIcon={<ScheduleIcon />}
                                    onClick={handleReschedule}
                                    size={isMobile ? "medium" : "small"}
                                >
                                    Reschedule
                                </Button>
                            )}
                            {canEdit && (
                                <Button
                                    variant="contained"
                                    startIcon={<EditIcon />}
                                    onClick={handleEdit}
                                    size={isMobile ? "medium" : "small"}
                                >
                                    Edit Post
                                </Button>
                            )}
                        </Stack>
                    </DrawerFooter>
                </DrawerContent>
            </Drawer>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Delete Post
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete this post? This action cannot be undone.
                        {post.platform_details?.some(detail => detail.is_posted) && (
                            <Box sx={{ mt: 2 }}>
                                <Alert severity="warning" size="small">
                                    Note: This will only delete the post from Solo Social. 
                                    Already published posts will remain on social platforms.
                                </Alert>
                            </Box>
                        )}
                    </DialogContentText>
                    {deleteError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {deleteError}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm} 
                        color="error" 
                        variant="contained"
                        disabled={deleting}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reschedule Dialog */}
            <RescheduleDialog
                open={rescheduleDialogOpen}
                onClose={() => setRescheduleDialogOpen(false)}
                post={post}
                onSuccess={handleRescheduleSuccess}
            />
        </>
    );
};

export default PostDetailsSlideover; 